import { gql } from "generated-graphql";

/**
 * All eight persisted Waste Activity fields (chosen + recommendation shown at
 * save time). Shared by the stepper shell query (read path) and the Waste
 * Activity step's upsert mutation so the cache row always carries the full
 * selection set.
 */
export const SUBMISSION_WASTE_ACTIVITY_FIELDS = gql(`
  fragment SubmissionWasteActivityFields on SubmissionWasteActivity {
    id
    submissionId
    federalGeneratorStatus
    stateGeneratorStatus
    federalWasteCodes
    stateWasteCodes
    recommendedFederalGeneratorStatus
    recommendedStateGeneratorStatus
    recommendedFederalWasteCodes
    recommendedStateWasteCodes
  }
`);

/**
 * Dedicated, slim query for the 8700-12 submission stepper shell. Kept separate
 * from the shared `GET_SUBMISSION_QUERY` (which the read-only view uses) so the
 * stepper only fetches what the shell/overview need: the single facility, the
 * submission type/method (to gate the stepper), and the ordered/filtered
 * `stepperSteps`.
 */
export const GET_SUBMISSION_STEPPER_QUERY = gql(`
  query GetSubmissionStepper($id: ID!) {
    submission(id: $id) {
      id
      title
      submissionType
      submissionMethod
      usesStepper
      status
      encampStatus
      dueDate
      notes
      reasonForSubmittal
      reviewRequired
      peerReviewRequired
      reviewMode
      # Review & sign step: signature type drives the signer/prep/sign affordances;
      # recipient is required by SubmissionInputForValidation.
      signatureType
      recipient
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      reviewers {
        id
        isStaff
        isSigner
        isRequired
        reviewOrder
        dueDate
        reviewerStatus
        completedAt
        acknowledgedAt
        signatureStatus
        signerNameOverride
        signerEmailOverride
        streetAddress1
        streetAddress2
        city
        state
        zip
        country
        submissionDocumentReviews {
          id
          createdAt
        }
        user {
          id
          email
          # Turn-taking (isActiveReviewer) phases by user type, matching the
          # backend's startReviewWorkflow routing.
          type
          person {
            first
            last
          }
        }
      }
      documents {
        ...SubmissionStepDocumentRow
      }
      revisedFromSubmission {
        id
        title
        createdAt
      }
      revisionSubmissions {
        id
        title
        createdAt
      }
      # Review & sign shows the manifest this submission reports on.
      processedManifestExceptionReport {
        id
        manifestTrackingNumber
      }
      facilitySubmissions {
        id
        facility {
          id
          name
          state
          streetAddress1
          customerFacilityId
          # EPA ID for the review surface; hasNoId rows are placeholders, not IDs.
          facilityAlternateIds {
            type
            value
            hasNoId
          }
        }
      }
      stepperSteps {
        step
        status
      }
      wasteActivity {
        ...SubmissionWasteActivityFields
      }
    }
  }
`);

/**
 * Fetches the frozen RCRA 8700-12 snapshot content for a submission. Used by the
 * read-only stepper view once a submission is frozen; the content is the
 * point-in-time `Resolved870012` payload (under `resolved`), so the read-only
 * view never reads the live tables. Returns null when no snapshot exists.
 */
export const GET_RCRA_8700_12_SNAPSHOT_CONTENT_QUERY = gql(`
  query Rcra870012SnapshotContent($submissionId: ID!) {
    rcra870012SnapshotContent(submissionId: $submissionId)
  }
`);

/**
 * Filing-notifications step data: the three optional 8700-12 notification rows
 * (§13 episodic event, §15 LQG site closure, §16 HSM notification) and their
 * children. Kept separate from the shell query so the step fetches its own data,
 * matching how `ContactsStep` owns its facility-contacts query.
 */
export const GET_SUBMISSION_FILING_NOTIFICATIONS_QUERY = gql(`
  query GetSubmissionFilingNotifications($id: ID!) {
    submission(id: $id) {
      id
      rcraEpisodicEvent {
        id
        isPlannedEpisodicEvent
        projectTypes
        beginningDate
        endDate
        otherDescription
        emergencyContact {
          id
          role
          isLocked
          person {
            id
            first
            last
            email
            phones {
              number
            }
          }
        }
        wasteLines {
          id
          wasteDescription
          estimatedQuantity
          quantityUnit
          federalWasteCodes
          stateWasteCodes
          sortOrder
        }
      }
      rcraSiteClosure {
        id
        closureType
        expectedClosureDate
        requestingNewClosureDate
        actualClosureDate
      }
      rcraHsmNotification {
        id
        isManagingHsm
        reason
        effectiveDate
        activities {
          id
          facilityCode
          wasteCodes
          estimatedShortTons
          landBasedUnit
          sortOrder
        }
      }
      consolidatedSites {
        id
        sortOrder
        epaIdNumber
        siteName
        streetAddress1
        streetAddress2
        city
        state
        zipCode
        country
        contact {
          id
          role
          isLocked
          person {
            id
            first
            last
            email
            phones {
              number
            }
          }
        }
      }
    }
  }
`);

/**
 * The facility's RCRA contacts, used to prefill the §13 episodic emergency
 * contact fields from an existing contact (the persisted value is still owned by
 * the submission — this only copies the person's details into the form).
 */
export const GET_FILING_NOTIFICATIONS_FACILITY_CONTACTS = gql(`
  query GetFilingNotificationsFacilityContacts($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      contacts(programArea: RCRA) {
        id
        person {
          id
          first
          last
          email
          phones {
            number
          }
        }
      }
    }
  }
`);

/**
 * Compliance-engine validator for the four submission-scoped Filing
 * Notifications sections (§13 episodic event, §14 LQG consolidation, §15 LQG
 * site closure, §16 HSM notification) in a single request. Each section is
 * optional, so the step sends only the sections whose gate is "Yes" (§14 is
 * always sent). Returns the combined `Issue[]` so the step can surface live
 * validation through `useValidatingForm`, matching how the Facility Profile step
 * validates via `facilityRcraProfileInputValidator`. §14's `isLqgConsolidator`
 * gate still lives on the facility RCRA profile, but its sites are
 * submission-scoped, so both are validated here.
 */
export const VALIDATE_SUBMISSION_RCRA_FILING_NOTIFICATIONS = gql(`
  query ValidateSubmissionRcraFilingNotifications(
    $submissionId: ID!
    $input: SubmissionRcraFilingNotificationsValidationInput!
  ) {
    submissionRcraFilingNotificationsInputValidator(submissionId: $submissionId, input: $input) {
      ...issue
    }
  }
`);

// Persisted §13/§14/§15/§16 compliance issues for the submission, used by the
// stepper nav/overview to count Filing Notifications issues without mounting the
// step. §14 consolidation issues are now submission-scoped too (modelName
// `SubmissionRcraConsolidation`), so they arrive here rather than on
// Facility.issues.
export const GET_SUBMISSION_RCRA_NOTIFICATION_ISSUES = gql(`
  query GetSubmissionRcraNotificationIssues($id: ID!) {
    submission(id: $id) {
      id
      rcraNotificationIssues {
        ...issue
      }
    }
  }
`);

/**
 * Submission-level compliance issues (`submissionSchema().validate`), fetched
 * separately from the stepper query so the nav can flag document-form steps
 * without widening that query. Document forms have no facility-issue steps, so
 * this is the only issue source they have.
 */
export const GET_SUBMISSION_ISSUES = gql(`
  query GetSubmissionIssues($id: ID!) {
    submission(id: $id) {
      id
      issues {
        ...issue
      }
    }
  }
`);

export const UPSERT_SUBMISSION_RCRA_EPISODIC_EVENT = gql(`
  mutation UpsertSubmissionRcraEpisodicEvent(
    $submissionId: ID!
    $input: SubmissionRcraEpisodicEventInput!
  ) {
    upsertSubmissionRcraEpisodicEvent(submissionId: $submissionId, input: $input) {
      id
    }
  }
`);

export const CLEAR_SUBMISSION_RCRA_EPISODIC_EVENT = gql(`
  mutation ClearSubmissionRcraEpisodicEvent($submissionId: ID!) {
    clearSubmissionRcraEpisodicEvent(submissionId: $submissionId)
  }
`);

export const UPSERT_SUBMISSION_RCRA_SITE_CLOSURE = gql(`
  mutation UpsertSubmissionRcraSiteClosure(
    $submissionId: ID!
    $input: SubmissionRcraSiteClosureInput!
  ) {
    upsertSubmissionRcraSiteClosure(submissionId: $submissionId, input: $input) {
      id
    }
  }
`);

export const CLEAR_SUBMISSION_RCRA_SITE_CLOSURE = gql(`
  mutation ClearSubmissionRcraSiteClosure($submissionId: ID!) {
    clearSubmissionRcraSiteClosure(submissionId: $submissionId)
  }
`);

// §14 sites are cleared via the empty-array `upsertSubmissionRcraConsolidation`
// path (isLqgConsolidator !== true sends `consolidatedSites: []`), so Oak has no
// consumer for a dedicated clear mutation. The server-side
// `clearSubmissionRcraConsolidation` mutation still exists for other callers.
export const UPSERT_SUBMISSION_RCRA_CONSOLIDATION = gql(`
  mutation UpsertSubmissionRcraConsolidation(
    $submissionId: ID!
    $input: SubmissionRcraConsolidationInput!
  ) {
    upsertSubmissionRcraConsolidation(submissionId: $submissionId, input: $input) {
      id
    }
  }
`);

export const UPSERT_SUBMISSION_RCRA_HSM_NOTIFICATION = gql(`
  mutation UpsertSubmissionRcraHsmNotification(
    $submissionId: ID!
    $input: SubmissionRcraHsmNotificationInput!
  ) {
    upsertSubmissionRcraHsmNotification(submissionId: $submissionId, input: $input) {
      id
    }
  }
`);

export const CLEAR_SUBMISSION_RCRA_HSM_NOTIFICATION = gql(`
  mutation ClearSubmissionRcraHsmNotification($submissionId: ID!) {
    clearSubmissionRcraHsmNotification(submissionId: $submissionId)
  }
`);

/**
 * One SubmissionDocument row with the display fields the document-form steps'
 * tables render. Shared by the Prepare Document and Submit & File queries so
 * both steps see the same row shape.
 */
export const SUBMISSION_STEP_DOCUMENT_ROW = gql(`
  fragment SubmissionStepDocumentRow on SubmissionDocument {
    id
    type
    externalDocumentId
    externalDocumentExpiresAt
    document {
      id
      title
      documentType
      storageLink
      fileExtension
    }
  }
`);

/**
 * Document rows for the Prepare Document step: every SubmissionDocument row —
 * submission, attachment, signed, rejected. Kept separate from the shell query
 * so the step owns its data (matching `ContactsStep`), and refetched after
 * each immediate document save.
 */
export const GET_SUBMISSION_DOCUMENTS_STEP_QUERY = gql(`
  query GetSubmissionDocumentsStep($id: ID!) {
    submission(id: $id) {
      id
      documents {
        ...SubmissionStepDocumentRow
      }
    }
  }
`);

/**
 * Submit & File step data: the filing details (recipient, method, due date,
 * payments) plus the document rows (for the ready-to-file state and the signed
 * submission block).
 */
export const GET_SUBMISSION_SUBMIT_AND_FILE_QUERY = gql(`
  query GetSubmissionSubmitAndFile($id: ID!) {
    submission(id: $id) {
      id
      recipient
      submissionMethod
      dueDate
      documents {
        ...SubmissionStepDocumentRow
      }
      payments {
        id
        amount
        payment {
          id
          amount
          payee
          paidAt
          status
        }
      }
    }
  }
`);

/**
 * Submission comment thread for the stepper Overview. Kept separate from the
 * shell query so comments (and their optimistic add/delete) live with the
 * Overview that renders them, and are fetched at every lifecycle stage.
 */
export const GET_SUBMISSION_STEPPER_COMMENTS_QUERY = gql(`
  query GetSubmissionStepperComments($id: ID!) {
    submission(id: $id) {
      id
      comments {
        ...CommentThread
      }
    }
  }
`);
